import React, { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from 'src/components/atoms/dialog'
import { Input } from 'src/components/atoms/inputs/Input'
import { PrimaryButton } from 'src/components/atoms/buttons/PrimaryButton'
import { GradientBorderButton } from 'src/components/atoms/buttons/GradientBorderButton'
import { api } from 'src/api/root'
import { folderNameSchema } from './folderNameSchema'
import { joinFolderPath } from 'src/enviroment/utils/joinFolderPath'

const createFolderSchema = z.object({
  folderName: folderNameSchema,
})

type CreateFolderFormData = z.infer<typeof createFolderSchema>

interface CreateFolderModalProps {
  isOpen: boolean
  onClose: () => void
  projectId: string
  parentPath: string | null
}

const CreateFolderModal: React.FC<CreateFolderModalProps> = ({ isOpen, onClose, projectId, parentPath }) => {
  const { mutateAsync: createFolder, isPending } = api.project.dataRoom.createFolder.useMutation({
    onSuccess: onClose,
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<CreateFolderFormData>({
    mode: 'onChange',
    resolver: zodResolver(createFolderSchema),
    defaultValues: { folderName: '' },
  })

  useEffect(() => {
    if (!isOpen) {
      reset()
    }
  }, [isOpen, reset])

  const onSubmit = (data: CreateFolderFormData) => {
    createFolder({ projectId, folderPath: joinFolderPath(parentPath, data.folderName.trim()) })
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed left-[50%] top-[50%] z-50 w-full max-w-[560px] translate-x-[-50%] translate-y-[-50%] rounded-lg border border-gray-500 bg-gray-900 p-7 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]"
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-bold leading-7 text-light-green-50">
              {parentPath ? `Create folder in "${parentPath}"` : 'Create new folder'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-2 flex flex-col gap-8">
            <Controller
              name="folderName"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Folder name"
                  placeholder="Enter folder name..."
                  autoFocus
                  errorMessage={errors.folderName?.message}
                  className="border-gray-700 bg-gray-800 text-base text-gray-50 placeholder:text-gray-200"
                />
              )}
            />

            <div className="flex justify-end gap-4">
              <DialogClose asChild>
                <GradientBorderButton gradientSize="md" textSize="base" type="button" onClick={onClose}>
                  Cancel
                </GradientBorderButton>
              </DialogClose>

              <PrimaryButton type="submit" disabled={!isValid || isPending} isLoading={isPending}>
                Create Folder
              </PrimaryButton>
            </div>
          </form>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}

export default CreateFolderModal
